// Castintech dashboard — Agency Flow view (Faz 30).
// Handoff zinciri canli gorsel: Telegram → Nexus (GM) → Yardimci → Agent →
// Toparlama. 3 endpoint agregat:
//   GET /castintech-api/handoffs/recent     (Faz 26 PR-B — handoff markerlar)
//   GET /castintech-api/rollups/recent      (Faz 26 PR-C — parent rollup zinciri)
//   GET /castintech-api/gm-gating/pending   (Faz 26 PR-A — GM bekleyen)
// 3 panel side-by-side; gerek duyarsa kullanici detay icin issue ID'lerine
// tiklayabilir (placeholder — drawer wiring sonradan).

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, ago, $ } from '../utils.js';

// Faz 47.2 S3.C — Agent ID/UUID/slug → readable name resolver. Handoff/rollup
// kayıtları bazen UUID, bazen slug, bazen Paperclip agent_id taşır. state.agents
// üzerinden eşleştirme yap; yoksa ilk 8 char + "…" yedek (önceki davranış).
function resolveAgentLabel(idOrSlug: string | null | undefined): string {
  if (!idOrSlug) return '-';
  const v = String(idOrSlug).trim();
  if (!v) return '-';
  // UUID veya slug eşleşmesi
  const lowV = v.toLowerCase();
  for (const a of state.agents) {
    if (!a) continue;
    const slug = (a as { slug?: string }).slug;
    if (a.id === v || (slug && slug.toLowerCase() === lowV) || (a.name && a.name.toLowerCase() === lowV)) {
      return a.name || v;
    }
  }
  // Slug-ish (lowercase, 3-12 char, alpha) → kapitalize
  if (/^[a-z][a-z0-9-]{2,11}$/i.test(v)) {
    return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
  }
  // UUID — kısalt
  if (v.length >= 32) return v.substring(0, 8) + '…';
  return v;
}

// Backend schema (Faz dashboard-m1 hizalama):
//   GET /handoffs/recent → { rows: [info_queue_row] }
//     metadata = { issue_id, from_agent, to_agent, from_agent_name,
//                  to_agent_name, comment_excerpt, requested_by }
//   GET /rollups/recent → { events: [info_queue_row], count }
//     metadata = { child_issue_id, parent_issue_id, child_agent_name, posted }
//   GET /gm-gating/pending → { pending: [gm_gating_pending_row], ... }
//     row = { id, parent_issue_id, target_agent_id, target_agent_name,
//             original_command, sub_issue_id, state, created_at, resolved_at }
interface RawAuditRow {
  id?: number;
  resource_id?: string;
  actor?: string;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  // legacy/test mock flat alanlar
  parent_id?: string;
  child_id?: string;
  source_agent?: string;
  target_agent?: string;
  marker?: string;
  status?: string;
  comment?: string;
}

interface HandoffRow {
  id?: number;
  issue_id: string;
  parent_id: string;
  child_id: string;
  source_agent: string;
  target_agent: string;
  marker: string;
  created_at: string;
}

interface RollupRow {
  id?: number;
  parent_id: string;
  child_id: string;
  status: string;
  comment: string;
  child_agent_name: string;
  created_at: string;
}

interface RawGmPendingRow {
  id?: number;
  parent_issue_id?: string;
  sub_issue_id?: string;
  target_agent_id?: string;
  target_agent_name?: string;
  original_command?: string;
  state?: string;
  created_at: string;
  // legacy
  issue_id?: string;
  identifier?: string;
  target_agent?: string;
  user_msg_preview?: string;
}
interface GmPendingRow {
  id?: number;
  issue_id: string;
  identifier: string;
  target_agent: string;
  user_msg_preview: string;
  created_at: string;
}

function asStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function normalizeHandoff(raw: RawAuditRow): HandoffRow {
  const md = raw.metadata || {};
  const issueId = asStr(md.issue_id) || raw.resource_id || raw.parent_id || '';
  return {
    id: raw.id,
    issue_id: issueId,
    parent_id: raw.parent_id || issueId,
    child_id: raw.child_id || issueId,
    source_agent: asStr(md.from_agent_name) || asStr(md.from_agent) || raw.source_agent || '',
    target_agent: asStr(md.to_agent_name) || asStr(md.to_agent) || raw.target_agent || '',
    marker: asStr(md.comment_excerpt) || raw.marker || '',
    created_at: raw.created_at,
  };
}

function normalizeRollup(raw: RawAuditRow): RollupRow {
  const md = raw.metadata || {};
  const parent = asStr(md.parent_issue_id) || raw.parent_id || raw.resource_id || '';
  const child = asStr(md.child_issue_id) || raw.child_id || '';
  const posted = md.posted;
  return {
    id: raw.id,
    parent_id: parent,
    child_id: child,
    status: raw.status || (posted === true ? 'posted' : (posted === false ? 'pending' : 'rolled-up')),
    comment: raw.comment || '',
    child_agent_name: asStr(md.child_agent_name),
    created_at: raw.created_at,
  };
}

function normalizeGmPending(raw: RawGmPendingRow): GmPendingRow {
  const issueId = raw.parent_issue_id || raw.issue_id || raw.sub_issue_id || '';
  return {
    id: raw.id,
    issue_id: issueId,
    identifier: raw.identifier || issueId || '—',
    target_agent: raw.target_agent_name || raw.target_agent_id || raw.target_agent || '',
    user_msg_preview: raw.original_command || raw.user_msg_preview || '',
    created_at: raw.created_at,
  };
}

let handoffs: HandoffRow[] = [];
let rollups: RollupRow[] = [];
let gmPending: GmPendingRow[] = [];

export async function loadAgencyFlow(): Promise<void> {
  try {
    const [hRes, rRes, gRes] = await Promise.all([
      apiFetch('/castintech-api/handoffs/recent?limit=30').catch(() => null),
      apiFetch('/castintech-api/rollups/recent?limit=30').catch(() => null),
      apiFetch('/castintech-api/gm-gating/pending?limit=30').catch(() => null),
    ]);
    if (hRes && hRes.ok) {
      const j = (await hRes.json()) as { rows?: RawAuditRow[]; handoffs?: RawAuditRow[]; events?: RawAuditRow[] };
      const arr = Array.isArray(j.rows) ? j.rows
        : (Array.isArray(j.handoffs) ? j.handoffs
        : (Array.isArray(j.events) ? j.events : []));
      handoffs = arr.map(normalizeHandoff);
    }
    if (rRes && rRes.ok) {
      const j = (await rRes.json()) as { rows?: RawAuditRow[]; rollups?: RawAuditRow[]; events?: RawAuditRow[] };
      const arr = Array.isArray(j.events) ? j.events
        : (Array.isArray(j.rows) ? j.rows
        : (Array.isArray(j.rollups) ? j.rollups : []));
      rollups = arr.map(normalizeRollup);
    }
    if (gRes && gRes.ok) {
      const j = (await gRes.json()) as { rows?: RawGmPendingRow[]; pending?: RawGmPendingRow[] };
      const arr = Array.isArray(j.pending) ? j.pending : (Array.isArray(j.rows) ? j.rows : []);
      gmPending = arr.map(normalizeGmPending);
    }
    renderAgencyFlow();
  } catch (e) {
    console.warn('loadAgencyFlow failed:', (e as Error).message);
  }
}

function renderHandoffSection(): string {
  if (!handoffs.length) return '<div class="audit-empty">Handoff yok.</div>';
  return handoffs.map(h => `
    <div class="audit-row">
      <span class="audit-badge indigo">handoff</span>
      <div class="audit-body">
        <div class="audit-msg">
          ${escHtml(resolveAgentLabel(h.source_agent))} → ${escHtml(resolveAgentLabel(h.target_agent))}
        </div>
        <div class="audit-meta-line">
          issue=${escHtml(h.issue_id || '—')}
          ${h.marker ? ' · ' + escHtml(h.marker.substring(0, 80)) : ''}
        </div>
      </div>
      <span class="audit-time" title="${escAttr(h.created_at)}">${ago(h.created_at)} once</span>
    </div>
  `).join('');
}

function renderRollupSection(): string {
  if (!rollups.length) return '<div class="audit-empty">Rollup yok.</div>';
  return rollups.map(r => `
    <div class="audit-row">
      <span class="audit-badge garnet">rollup</span>
      <div class="audit-body">
        <div class="audit-msg">
          parent=${escHtml(r.parent_id || '—')} ← child=${escHtml(r.child_id || '—')} (${escHtml(r.status || '?')})
        </div>
        <div class="audit-meta-line">
          ${r.child_agent_name ? '@' + escHtml(r.child_agent_name) : ''}
          ${r.comment ? ' · ' + escHtml(r.comment.substring(0, 200)) : ''}
        </div>
      </div>
      <span class="audit-time" title="${escAttr(r.created_at)}">${ago(r.created_at)} once</span>
    </div>
  `).join('');
}

function renderGmPendingSection(): string {
  if (!gmPending.length) return '<div class="audit-empty">GM kuyrugunda is yok.</div>';
  return gmPending.map(g => `
    <div class="audit-row">
      <span class="audit-badge topaz">GM</span>
      <div class="audit-body">
        <div class="audit-msg">
          <b>${escHtml(g.identifier || g.issue_id)}</b> hedef=${escHtml(resolveAgentLabel(g.target_agent))}
        </div>
        ${g.user_msg_preview ? `<div class="audit-meta-line">${escHtml(g.user_msg_preview.substring(0, 200))}</div>` : ''}
      </div>
      <span class="audit-time" title="${escAttr(g.created_at)}">${ago(g.created_at)} once</span>
    </div>
  `).join('');
}

export function renderAgencyFlow(): void {
  const root = $('agency-flow-content');
  const sub = $('agency-flow-sub');
  if (!root) return;
  if (sub) sub.textContent = `${gmPending.length} GM pending · ${handoffs.length} handoff · ${rollups.length} rollup`;
  root.innerHTML = `
    <div class="health-grid" style="grid-template-columns:repeat(auto-fit,minmax(320px,1fr));">
      <div class="health-card">
        <div class="hc-title">GM Gating (pending)</div>
        <div class="hc-body">${renderGmPendingSection()}</div>
      </div>
      <div class="health-card">
        <div class="hc-title">Handoffs (son)</div>
        <div class="hc-body">${renderHandoffSection()}</div>
      </div>
      <div class="health-card">
        <div class="hc-title">Rollups (son)</div>
        <div class="hc-body">${renderRollupSection()}</div>
      </div>
    </div>
  `;
}
